import { useState } from 'react';
import { ChevronRight, Plus, FileUp, Calendar, Pencil, Users as UsersIcon, Layers, Settings, MessageCircle, type LucideIcon } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { Project, Role } from '@/types/api';
import { PaymentModal } from '@/components/PaymentModal';

interface Props {
  project: Project;
  snapshotLabel: string;
  version: number;
  count: number;
  role: Role;
  aiSwap: boolean;
  onTitleClick: () => void;
  onCreate: () => void;
  onOpenAISummary: () => void;
  onToggleAiSwap: () => void;
  /** 13.3: заказчику — чип «Проектировщик» в шапке (клик → окно). */
  onOpenDesigner?: () => void;
  /** 14.1: заказчику — чип «Загрузка и редакции» в шапке (клик → окно). */
  onOpenDocs?: () => void;
  /** IA заказчика: ⚙ в шапке → лист «Настройки проекта» (проектировщик/тариф/паспорт). */
  onOpenSettings?: () => void;
  /** 15.D: заказчику — иконка-чат с бейджем непрочитанного → список консультаций. */
  onOpenConsultations?: () => void;
}

const CTA_BY_ROLE: Record<Role, { icon: LucideIcon; label: string }> = {
  curator:  { icon: Plus,    label: 'Создать замечание' },
  expert:   { icon: Plus,    label: 'Создать замечание' },
  gip:      { icon: FileUp,  label: 'Импорт ответов из Word' },
  admin:    { icon: Plus,    label: 'Создать замечание' },
  customer: { icon: Plus,    label: 'Замечание приёмки' },
};

function calcDaysLeft(deadlineAt: string | null | undefined): number | null {
  if (!deadlineAt) return null;
  try {
    const dl = new Date(deadlineAt);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.round((dl.getTime() - today.getTime()) / 86400000);
  } catch {
    return null;
  }
}

/** Header проекта: метаданные + deadline counter + 3 действия. CTA контекстен под роль. */
export function ProjectHeader({ project, snapshotLabel, version, role, onTitleClick, onCreate, onOpenDesigner, onOpenDocs, onOpenSettings, onOpenConsultations }: Props) {
  const cta = CTA_BY_ROLE[role];
  const CtaIcon = cta.icon;
  const titleClean = stripCode(project.name);
  const [editingDeadline, setEditingDeadline] = useState(false);
  const [deadlineInput, setDeadlineInput] = useState(project.deadline_at?.slice(0, 10) || '');
  const [payOpen, setPayOpen] = useState(false); // 15.E ч.2 оплата тарифа
  const qc = useQueryClient();

  // 13.3: статус проектировщика для чипа (только когда чип нужен).
  const designersQ = useQuery<{ members: { role: string; full_name?: string | null; email: string }[] }>({
    queryKey: ['auth-members', project.id],
    queryFn: () => api.get(`/projects/${project.id}/auth-members`),
    enabled: !!onOpenDesigner,
    retry: false,
  });
  const designer = (designersQ.data?.members || []).find((m) => m.role === 'gip');

  // 15.D: непрочитанные ответы координатора для бейджа на иконке-чате (заказчику).
  const unreadQ = useQuery<{ unread: number }>({
    queryKey: ['consultations-unread', project.id],
    queryFn: () => api.get<{ unread: number }>(`/projects/${project.id}/consultations/unread-count`),
    enabled: !!onOpenConsultations,
    refetchInterval: 20000,
    retry: false,
  });
  const unread = unreadQ.data?.unread ?? 0;

  const daysLeft = calcDaysLeft(project.deadline_at);
  const deadlineColor = daysLeft === null ? '#6B7280'
    : daysLeft < 0 ? '#EF4444'
    : daysLeft <= 7 ? '#EF4444'
    : daysLeft <= 30 ? '#F59E0B'
    : '#10B981';
  const deadlineLabel = daysLeft === null ? 'Срок не задан'
    : daysLeft < 0 ? `Просрочено ${Math.abs(daysLeft)} дн.`
    : daysLeft === 0 ? 'Сегодня последний день'
    : `${daysLeft} дн. до сдачи`;

  const deadlineMut = useMutation({
    mutationFn: (v: string | null) =>
      api.patch<{ deadline_at: string | null }>(`/projects/${project.id}/deadline`, { deadline_at: v || null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project', project.id] });
      setEditingDeadline(false);
    },
  });

  // Фаза 10 (2026-07-04): срок сдачи задаёт заказчик (он готовит проект к
  // экспертизе) — он и куратор/админ. От этого срока считается срочность задач ГИП.
  const canEditDeadline = role === 'curator' || role === 'admin' || role === 'customer';


  return (
    <>
    {payOpen && <PaymentModal projectId={project.id} onClose={() => setPayOpen(false)} />}
    <section className="bg-surface border-b border-border" style={{ padding: '24px 0' }}>
      <div className="mx-auto grid grid-cols-12" style={{ maxWidth: 1280, paddingLeft: 24, paddingRight: 24, gap: 24 }}>
        <div className="col-span-8">
          <button
            type="button"
            onClick={onTitleClick}
            className="text-left flex items-start"
            style={{ background: 'transparent', padding: 0, gap: 8, border: 'none', cursor: 'pointer' }}
            aria-label="Открыть паспорт объекта"
          >
            <h2
              className="font-heading text-ink"
              style={{ fontSize: 28, fontWeight: 600, lineHeight: 1.2, letterSpacing: '-0.01em' }}
            >{titleClean}</h2>
            <ChevronRight size={20} stroke="#6B7280" style={{ marginTop: 6, flexShrink: 0 }} />
          </button>
          {/* Строка 1: редакция + вход в документы. */}
          <div className="flex items-center mt-2" style={{ gap: 12, flexWrap: 'wrap' }}>
            <p className="text-muted" style={{ fontSize: 14, margin: 0 }}>
              {/* 11.6: до первой загрузки редакции нет — не показываем «Редакция №0 от нет». */}
              {version > 0
                ? <>Редакция №{version} от {snapshotLabel}</>
                : <>Документы ещё не загружены</>}
            </p>
            {onOpenDocs && (
              <button
                type="button"
                onClick={onOpenDocs}
                className="inline-flex items-center rounded-pill"
                style={{ gap: 6, padding: '4px 10px', fontSize: 12, fontWeight: 500, background: '#F3F4F6', color: '#0A1628', border: '1px solid #E5E7EB', cursor: 'pointer' }}
                title="Загрузка документов, редакции и сравнение"
              >
                <Layers size={12} stroke="#F59E0B" />
                {version > 0 ? 'Открыть документы и редакции' : 'Загрузить документы'}
              </button>
            )}
          </div>

          {/* Строка 2 (в сторону): срок · проектировщик · оплата. */}
          <div className="flex items-center mt-3" style={{ gap: 10, flexWrap: 'wrap' }}>
            {/* Deadline counter */}
            {!editingDeadline ? (
              <button
                type="button"
                onClick={() => canEditDeadline ? setEditingDeadline(true) : undefined}
                className="inline-flex items-center rounded-pill"
                style={{
                  gap: 5, padding: '4px 10px', fontSize: 12, fontWeight: 600,
                  background: `${deadlineColor}18`, color: deadlineColor,
                  border: `1px solid ${deadlineColor}44`,
                  cursor: canEditDeadline ? 'pointer' : 'default',
                  transition: 'background 0.15s, border-color 0.15s',
                }}
                title={canEditDeadline ? 'Нажмите, чтобы изменить срок сдачи' : 'Срок сдачи'}
                onMouseEnter={(e) => { if (canEditDeadline) e.currentTarget.style.background = `${deadlineColor}2A`; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = `${deadlineColor}18`; }}
              >
                <Calendar size={12} />
                {deadlineLabel}
                {canEditDeadline && (
                  <Pencil size={10} style={{ opacity: 0.7, marginLeft: 2 }} />
                )}
              </button>
            ) : (
              <div className="inline-flex items-center" style={{ gap: 6 }}>
                <input
                  type="date"
                  value={deadlineInput}
                  onChange={(e) => setDeadlineInput(e.target.value)}
                  style={{ padding: '3px 8px', fontSize: 12, border: '1px solid #E5E7EB', borderRadius: 6, outline: 'none' }}
                />
                <button
                  type="button"
                  onClick={() => deadlineMut.mutate(deadlineInput || null)}
                  disabled={deadlineMut.isPending}
                  style={{ padding: '3px 10px', fontSize: 12, fontWeight: 600, background: '#F59E0B', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}
                >
                  {deadlineMut.isPending ? '…' : 'Сохранить'}
                </button>
                <button
                  type="button"
                  onClick={() => setEditingDeadline(false)}
                  style={{ padding: '3px 8px', fontSize: 12, background: 'transparent', color: '#6B7280', border: '1px solid #E5E7EB', borderRadius: 6, cursor: 'pointer' }}
                >
                  Отмена
                </button>
                {project.deadline_at && (
                  <button
                    type="button"
                    onClick={() => deadlineMut.mutate(null)}
                    style={{ padding: '3px 8px', fontSize: 12, background: 'transparent', color: '#EF4444', border: '1px solid #EF4444', borderRadius: 6, cursor: 'pointer' }}
                  >
                    Сбросить
                  </button>
                )}
              </div>
            )}

            {/* Роль и тариф перенесены в меню профиля (AC, справа вверху). */}

            {/* 15.E ч.2: тариф не активирован → кнопка оплаты (СБП-QR). Только
                заказчику (плательщик); куратор/админ активируют вручную. */}
            {!project.tariff && role === 'customer' && (
              <button
                type="button"
                onClick={() => setPayOpen(true)}
                className="inline-flex items-center rounded-pill"
                style={{
                  gap: 5, padding: '4px 12px', fontSize: 12, fontWeight: 600,
                  background: '#0A1628', color: '#fff', border: 'none', cursor: 'pointer',
                }}
                title="Оплатить тариф по СБП и активировать проверки"
              >
                Оплатить
              </button>
            )}

            {/* 13.3: чип «Проектировщик» — вход в окно (пригласить/сменить/сбросить). */}
            {onOpenDesigner && (
              <button
                type="button"
                onClick={onOpenDesigner}
                className="inline-flex items-center rounded-pill"
                style={{
                  gap: 6, padding: '4px 10px', fontSize: 12, fontWeight: 500,
                  background: designer ? '#F3F4F6' : 'transparent',
                  color: designer ? '#0A1628' : '#6B7280',
                  border: `1px ${designer ? 'solid #E5E7EB' : 'dashed #CBD5E1'}`,
                  cursor: 'pointer',
                }}
                title="Проектировщик — пригласить, сменить, сбросить пароль"
              >
                <UsersIcon size={12} stroke={designer ? '#F59E0B' : '#9CA3AF'} />
                {designer ? `Проектировщик: ${designer.full_name || designer.email}` : 'Назначить проектировщика'}
              </button>
            )}
          </div>
        </div>
        <div className="col-span-4 flex justify-end items-start flex-wrap" style={{ gap: 8 }}>
          {/* 15.D: иконка-чат «Консультации специалиста» с бейджем непрочитанного. */}
          {onOpenConsultations && (
            <button
              type="button"
              onClick={onOpenConsultations}
              className="inline-flex items-center justify-center rounded-btn"
              style={{ position: 'relative', width: 44, height: 44, background: '#fff', border: '1px solid #E5E7EB', cursor: 'pointer', color: '#6B7280' }}
              title="Консультации специалиста"
              aria-label={unread > 0 ? `Консультации специалиста, непрочитанных: ${unread}` : 'Консультации специалиста'}
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = '#CBD5E1')}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = '#E5E7EB')}
            >
              <MessageCircle size={18} />
              {unread > 0 && (
                <span style={{ position: 'absolute', top: -6, right: -6, minWidth: 18, height: 18, padding: '0 4px', borderRadius: 9, background: '#EF4444', color: '#fff', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {unread}
                </span>
              )}
            </button>
          )}
          {/* ⚙ тихая дверь в лист «Настройки проекта» (проектировщик/тариф/паспорт). */}
          {onOpenSettings && (
            <button
              type="button"
              onClick={onOpenSettings}
              className="inline-flex items-center justify-center rounded-btn"
              style={{ width: 44, height: 44, background: '#fff', border: '1px solid #E5E7EB', cursor: 'pointer', color: '#6B7280' }}
              title="Настройки проекта"
              aria-label="Настройки проекта"
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = '#CBD5E1')}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = '#E5E7EB')}
            >
              <Settings size={18} />
            </button>
          )}
          <button
            type="button"
            onClick={onCreate}
            className="inline-flex items-center rounded-btn"
            style={{
              padding: '12px 20px',
              background: '#F59E0B',
              color: '#0A1628',
              fontWeight: 600, fontSize: 14, gap: 8, cursor: 'pointer', border: 'none',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#D97706')}
            onMouseLeave={(e) => (e.currentTarget.style.background = '#F59E0B')}
          >
            <CtaIcon size={18} />
            <span>{cta.label}</span>
          </button>
        </div>
      </div>
    </section>
    </>
  );
}

export function shortCode(name: string): string {
  const m = name.match(/^[А-ЯA-Z]{2,5}[-\s]?\d{1,6}/);
  return m ? m[0] : (name.split(/[—\-]/)[0].trim().slice(0, 12) || 'Проект');
}
export function stripCode(name: string): string {
  return name.replace(/^[А-ЯA-Z]{2,5}[-\s]?\d{1,6}\s*[—\-:]\s*/, '').trim() || name;
}
